import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { payrollLeave } from '../../api';
import { LeaveDialog, LEAVE_TYPES } from './LeaveDialog';

const BADGE = { annual: 'bg-emerald-50 text-emerald-800', sick: 'bg-sky-50 text-sky-800', family: 'bg-violet-50 text-violet-800', unpaid: 'bg-red-50 text-red-700' };
const LABEL = Object.fromEntries(LEAVE_TYPES);
const COLS = [['annual', 'Annual'], ['sick', 'Sick'], ['family', 'Family']];

const Bal = ({ v, of, testid }) => (
  <td className="px-4 py-3 text-right tabular-nums" data-testid={testid}>
    <span className={`font-semibold ${v < 0 ? 'text-red-700' : 'text-stone-900'}`}>{v}</span>
    <span className="text-[11px] text-stone-400"> / {of}</span>
  </td>
);

const History = ({ entries, onDelete }) => (
  <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="leave-history">
    <div className="p-5 border-b border-stone-100 font-semibold text-stone-900">Leave history</div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider"><tr>{['Employee', 'Type', 'Dates', 'Days', 'Note', ''].map((h, i) => <th key={h || i} className={`px-4 py-2 font-medium ${h === 'Days' ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-stone-100">
          {entries.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-500">No leave recorded yet.</td></tr>}
          {entries.map((x) => (
            <tr key={x.id} data-testid={`leave-entry-${x.id}`}>
              <td className="px-4 py-2.5 font-medium text-stone-900">{x.employeeName}</td>
              <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full ${BADGE[x.type]}`}>{LABEL[x.type]}</span></td>
              <td className="px-4 py-2.5 text-stone-600">{x.startDate}{x.endDate !== x.startDate ? ` → ${x.endDate}` : ''}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{x.days}</td>
              <td className="px-4 py-2.5 text-stone-500">{x.note}</td>
              <td className="px-4 py-2.5 text-right"><button onClick={() => onDelete(x)} className="text-stone-400 hover:text-red-700" title="Delete" data-testid={`leave-delete-${x.id}`}><Trash2 size={15}/></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const LeaveTab = ({ clientId, toast }) => {
  const [asAt, setAsAt] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);
  const load = () => payrollLeave.get(clientId, asAt).then(setData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [clientId, asAt]);
  const del = (x) => window.confirm(`Delete ${x.days} day(s) ${LABEL[x.type].toLowerCase()} for ${x.employeeName}?`) &&
    payrollLeave.remove(x.id).then(() => { toast({ title: 'Leave entry deleted' }); load(); })
      .catch((e) => toast({ title: 'Could not delete', description: e?.response?.data?.detail || 'Try again.', variant: 'destructive' }));
  const active = data?.employees.filter((e) => e.active) || [];

  return (
    <div className="space-y-6" data-testid="payroll-leave-tab">
      <div className="flex items-end gap-3 flex-wrap">
        <div><div className="text-xs text-stone-500 mb-1">Balances as at</div><Input type="date" value={asAt} onChange={(e) => e.target.value && setAsAt(e.target.value)} className="h-9 w-44" data-testid="leave-asat-input"/></div>
        <Button size="sm" disabled={!active.length} onClick={() => setDialog({ preset: null })} className="ml-auto bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="leave-record-btn"><Plus size={14} className="mr-1.5"/> Record leave</Button>
      </div>
      {!data ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div> : (
        <>
          <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
            <div className="p-5 border-b border-stone-100">
              <div className="font-semibold text-stone-900">Leave balances</div>
              <div className="text-xs text-stone-500 mt-0.5">BCEA: annual accrues monthly (15 days/yr = 1.25/month), sick 30 days per 3-year cycle, family 3 days per year of service. Balances print on every payslip.</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leave-balances-table">
                <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider"><tr><th className="text-left px-4 py-3 font-medium">Employee</th>{COLS.map(([, l]) => <th key={l} className="text-right px-4 py-3 font-medium">{l}</th>)}<th/></tr></thead>
                <tbody className="divide-y divide-stone-100">
                  {data.employees.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-stone-500">Add employees first.</td></tr>}
                  {data.employees.map((e) => (
                    <tr key={e.id} className={e.active ? '' : 'opacity-50'} data-testid={`leave-row-${e.id}`}>
                      <td className="px-4 py-3 font-medium text-stone-900">{e.name}<div className="text-[11px] font-normal text-stone-500">{e.employeeNumber}{e.opening ? ` · opening balances from ${e.opening.asAt}` : ''}</div></td>
                      {COLS.map(([k]) => <Bal key={k} v={e.balances[k]} of={e.entitlements[k]} testid={`leave-balance-${k}-${e.id}`}/>)}
                      <td className="px-4 py-3 text-right">{e.active && <Button size="sm" variant="outline" onClick={() => setDialog({ preset: e.id })} data-testid={`leave-add-${e.id}`}><Plus size={13} className="mr-1"/> Leave</Button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <History entries={data.entries} onDelete={del}/>
        </>
      )}
      <LeaveDialog open={!!dialog} preset={dialog?.preset} onOpenChange={(o) => !o && setDialog(null)} clientId={clientId} employees={active} onSaved={load} toast={toast}/>
    </div>
  );
};
